import { useMemo } from 'react';
import { Card, Dropdown, makeStyles, mergeClasses, Option, tokens } from '@fluentui/react-components';
import {Dismiss16Filled} from '@fluentui/react-icons';

import { useAutomationRuleActions } from './use-actions';
import { initialAddCommentToWorkItem } from './add-comment/use-add-comment';
import { sortByDate } from '../utils/sort-by-date';

import { ActionItemType } from './types';
import { initialEditWorkItem } from './edit-work-item/use-edit-work-item';

import FormDividerActions from './divider';
import EditWorkItem from './edit-work-item';
import AddCommentToWorkItem from './add-comment';
import SendNotificationToSlack from './send-notification-to-slack';
import { initialSendNotificationToSlack } from './send-notification-to-slack/use-send-notification-to-slack';
import { initialSendEmailNotification } from './send-email/use-send-email-notification';
import SendEmailNotification from './send-email';
import SendTeamsNotification from './send-teams-notification';
import { initialSendTeamsNotification } from './send-teams-notification/use-send-teams-notification';

import { useAddCommentForRelationships } from './add-comment/use-add-comment-for-relationships';
import { useEditWorkItemRelationship } from './edit-work-item/use-edit-workitem-relationship';
import ActionRelationshipInput from 'src/components/shared/action-relationship-input';
export const actionTypes = {
	editWorkItem: 'edit-work-item',
	addCommentToWorkItem: 'add-comment',
	sendNotificationToSlack: 'slack-notification',
	sendEmailNotification: 'send-email-notification',
	sendMessageToTeams: 'send-teams-notification',
};

export const actionOptions = [
	{ label: 'Edit work item', value: actionTypes.editWorkItem },
	{ label: 'Add comment to work item', value: actionTypes.addCommentToWorkItem },
	{ label: 'Send email', value: actionTypes.sendEmailNotification },
	{ label: 'Send Slack message', value: actionTypes.sendNotificationToSlack },
	{ label: 'Send Teams message', value: actionTypes.sendMessageToTeams },
];

const ActionItem = (props: { actionId: string; action: string }) => {
	const classes = useStyles();
	
	const {actions, setActions, actionValidationErrors, resetValidationErrors, removeActionItem} = useAutomationRuleActions();

	const addCommentForRelationships = useAddCommentForRelationships({actionId: props.actionId});
	const editWorkItemForRelationships = useEditWorkItemRelationship({actionId: props.actionId});

	const currentActionValidations = useMemo(() => {
		const found = actionValidationErrors.find(item => item.actionId === props.actionId);
		return found;
	}, [props.actionId, actionValidationErrors]);

	const subForm = useMemo(() => {
		if (props.action === actionTypes.sendMessageToTeams) {
			return (
				<SendTeamsNotification
					actionId={props.actionId}
					validations={currentActionValidations?.sendTeamsNotification || null}
				/>
			);
		}

		if (props.action === actionTypes.sendEmailNotification) {
			return (
				<SendEmailNotification
					actionId={props.actionId}
					validations={currentActionValidations?.sendEmailNotification || null}
				/>
			);
		}

		if (props.action === actionTypes.sendNotificationToSlack) {
			return (
				<SendNotificationToSlack
					actionId={props.actionId}
					validations={currentActionValidations?.sendNotificationToSlack || null}
				/>
			);
		}

		if (props.action === actionTypes.addCommentToWorkItem) {
			return (
				<AddCommentToWorkItem
					actionId={props.actionId}
					validations={currentActionValidations?.addCommentToWorkItem || null}
				/>
			);
		}
		if (props.action === actionTypes.editWorkItem) {
			return (
				<EditWorkItem
					actionId={props.actionId}
					validations={currentActionValidations?.editWorkItem || null}
				/>
			);
		}

		return null;
	}, [props.action, props.actionId, actionValidationErrors]);

	return (
		<>
			<div className={classes.deleteButtonRoot}>
				<button
					className={classes.deleteBtn}
					onClick={(ev) => {
						ev.preventDefault();
						removeActionItem(props.actionId);
					}}
				>
					<Dismiss16Filled />
				</button>
			</div>
			<div style={{ width: 'fit-content' }}>
				<Dropdown
					className={mergeClasses(
						classes.dropdown,
						classes.triggerTypeDropdown,
						currentActionValidations?.action ? classes.dropdownWithError : ''
					)}
					listbox={{
						style: { minWidth: 'max-content' }
					}}
					appearance='underline'
					placeholder='Then'
					positioning="below-start"
					value={actionOptions.find(item => item.value === props.action)?.label || ''}
					onOptionSelect={async (_, data) => {
						resetValidationErrors();
						const current = actions.find(item => item.id === props.actionId) as ActionItemType;
						const others = actions.filter(item => item.id !== props.actionId); 

						const newCurrent: ActionItemType = {
							...current,
							editWorkItem: null,
							addCommentToWorkItem: null,
							sendTeamsNotification: null,
							sendEmailNotification: null,
							sendNotificationToSlack: null,
							action: data.optionValue,
						};

						if (data.optionValue === actionTypes.sendMessageToTeams) {
							newCurrent.sendTeamsNotification = initialSendTeamsNotification;
						}

						if (data.optionValue === actionTypes.sendEmailNotification) {
							setActions([
								...others,
								{
									...newCurrent,
									sendEmailNotification: {
										...initialSendEmailNotification,
									},
								}
							]);

							newCurrent.sendEmailNotification = {
								...initialSendEmailNotification,
							};
						}

						if (data.optionValue === actionTypes.sendNotificationToSlack) {
							newCurrent.sendNotificationToSlack = {...initialSendNotificationToSlack};
						}

						if (data.optionValue === actionTypes.addCommentToWorkItem) {
							newCurrent.addCommentToWorkItem = {...initialAddCommentToWorkItem};
						}

						if (data.optionValue === actionTypes.editWorkItem) {
							setActions([
								...others,
								{
									...newCurrent,
									editWorkItem: {...initialEditWorkItem},
								}
							]);

							newCurrent.editWorkItem = {
								...initialEditWorkItem,
							};
						}
					
						setActions([...others, newCurrent]);
					}}
				>
					{actionOptions.map((actionItem) => (
						<Option key={actionItem.value} value={actionItem.value}>
							{actionItem.label}
						</Option>
					))}
				</Dropdown>
				{
					props.action === actionTypes.addCommentToWorkItem && (
						<ActionRelationshipInput
							actionId={props.actionId}
							onOptionSelect={addCommentForRelationships.onOptionsSelect}
						/>
					)
				}
				{
					props.action === actionTypes.editWorkItem && (
						<ActionRelationshipInput
							actionId={props.actionId}
							onOptionSelect={editWorkItemForRelationships.onOptionsSelect}
						/>
					)
				}
			</div>
			{
				currentActionValidations?.action && (
					<span className={classes.helperTextForError}>
						{currentActionValidations?.action}
					</span>
				)
			}
			{subForm}
		</>
	);
};

export default function AutomationRuleActions() {
	const { actions } = useAutomationRuleActions();

	return actions
		.sort((a, b) => sortByDate(a.createdAt, b.createdAt))
		.map((action, idx) => (
			<>
				<Card style={{ overflow: 'visible' }}>
					<ActionItem
						actionId={action.id}
						action={action.action as string}
					/>
				</Card>
				<FormDividerActions isLast={idx === actions.length - 1} />
			</>
		));
}

const useStyles = makeStyles({
	root: {},
	row: {},
	dropdown: {
		minWidth: 'auto',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralShadowAmbientLighter
		},
	},
	triggerTypeDropdown: {
		// maxWidth: '160px'
	},
	subForm: {
		width: '100%',
		display: 'flex',
	},
	helperTextForError: {
		width: '100%',
		color: tokens.colorPaletteRedBorder2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
		padding: '0px 4px'
	},
	dropdownWithError: {
		backgroundColor: tokens.colorPaletteRedBackground2,
		'& button': {
			color: tokens.colorPaletteRedBorder2,
		},
		'& svg': {
			color: tokens.colorPaletteRedBorder2,
		},
		':hover': {
			backgroundColor: tokens.colorPaletteRedBackground2,
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
		}
	},
	deleteButtonRoot: {
		position: 'absolute',
		zIndex: 1,
		right: 0,
		top: 0,
		padding: '8px'
	},
	deleteBtn: {
		padding: '2px',
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		color: tokens.colorNeutralStrokeAccessible
	},
});